/**
 * Performs monotone cubic interpolation on an array of downsampled measurements to a new length.
 * @param {number[]} downsampledData The original array of measurements.
 * @param {number} targetLength The desired length of the new array.
 * @param {number} lengthOfDownsampledSection The length of the section of downsampledData that was downsampled by the LTTB algorithm. The remaining part of the array contain non-downsampled datapoints and are therefore appended as individual datapoints.
 * @returns {number[]} A new array with interpolated values.
 */
export const interpolateSenseDatapoints = (downsampledData, targetLength, lengthOfDownsampledSection) => {
    // Only interpolate if data is in fact downsampled
    if (downsampledData.length == targetLength)
        return downsampledData;
    // Separate data that is downsampled from data that is variably added to the string
    // Adjust targetLength to interpolationLength by substracting the amount of variably added measurements
    const downsampledDataToInterpolate = downsampledData.slice(0, lengthOfDownsampledSection);
    const downsampledDataToAppend = downsampledData.slice(lengthOfDownsampledSection);
    const originalLength = downsampledDataToInterpolate.length;
    if (originalLength >= targetLength) {
        throw new Error('Target length must be greater than the length of the input array.');
    }
    const adjustedTargetLength = targetLength - downsampledDataToAppend.length;
    const interpolatedData = [];
    // Calculate the slopes
    const slopes = new Array(originalLength - 1);
    for (let i = 0; i < originalLength - 1; i++) {
        slopes[i] = downsampledDataToInterpolate[i + 1] - downsampledDataToInterpolate[i];
    }
    // Calculate the tangents
    const tangents = new Array(originalLength);
    tangents[0] = slopes[0];
    tangents[originalLength - 1] = slopes[originalLength - 2];
    for (let i = 1; i < originalLength - 1; i++) {
        if (slopes[i] * slopes[i - 1] > 0) {
            tangents[i] = (2 * slopes[i - 1] * slopes[i]) / (slopes[i - 1] + slopes[i]);
        }
        else {
            tangents[i] = 0;
        }
    }
    // Perform interpolation
    const step = (originalLength - 1) / (adjustedTargetLength - 1);
    for (let i = 0; i < adjustedTargetLength; i++) {
        const index = i * step;
        const lowerIndex = Math.floor(index);
        const upperIndex = Math.ceil(index);
        if (lowerIndex === upperIndex) {
            interpolatedData.push(downsampledDataToInterpolate[lowerIndex]);
        }
        else {
            const t = index - lowerIndex;
            const h00 = (1 + 2 * t) * (1 - t) * (1 - t);
            const h10 = t * (1 - t) * (1 - t);
            const h01 = t * t * (3 - 2 * t);
            const h11 = t * t * (t - 1);
            const interpolatedValue = h00 * downsampledDataToInterpolate[lowerIndex] +
                h10 * tangents[lowerIndex] +
                h01 * downsampledDataToInterpolate[upperIndex] +
                h11 * tangents[upperIndex];
            interpolatedData.push(parseFloat(interpolatedValue.toFixed(1)));
        }
    }
    // Merge interpolated data with variably added measurements
    return [...interpolatedData, ...downsampledDataToAppend];
};
